import React from "react";
import {
    DecoratorNode,
    NodeKey,
    LexicalNode,
    LexicalEditor,
    SerializedLexicalNode,
    Spread,
} from "lexical";
import { observer } from "mobx-react-lite";
import { chatState } from "@/app/chatState";
import { QuoteIcon } from "@/components/UI/svgs";
import { STREAMING_QUOTE, StreamingQuoteContext } from "./streaming";

// A quote renders synchronously as plain text: a nested editor mounts empty and fills in a frame
// later, which collapses the quote's height every time the surrounding message is re-imported.
function stripInlineMarkdown(text: string): string {
    return text
        .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
        .replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm, "")
        .replace(/\*\*|__|`/g, "");
}


// LEGACY this is from when we used anthropic's internal citations API
export type ToolCitation = {
    citationType: "tool";
    tool_use_id: string;
    document: {
        type: string;
        id: number;
        name: string;
        tool_use_id?: string;
    };
    cited_text: string;
}

export type ArtifactCitation = {
    citationType: "artifact";
    artifact_id: number;
    start_line?: number;
    end_line?: number;
    cited_text?: string;
    // A link is a reference with no cited_text: a pure pointer to the object,
    // rendered as an inline link rather than a quoted block.
    isLink?: boolean;
}

export type EventCitation = {
    citationType: "event";
    event_id: number;
    start_line?: number;
    end_line?: number;
    cited_text?: string;
    isLink?: boolean;
}

export type DocumentCitation = {
    citationType: "document";
    document_id: number;
    start_line?: number;
    end_line?: number;
    cited_text?: string;
    isLink?: boolean;
}

/** A quote from this conversation's history; the times are epoch seconds of the quoted messages. */
export type HistoryCitation = {
    citationType: "history";
    conversation_id: number;
    start_time: number;
    end_time: number;
    cited_text?: string;
}

export type CitationPayload = ToolCitation | ArtifactCitation | EventCitation | DocumentCitation | HistoryCitation;

/** True when the payload is a link: a source reference with no quoted text. */
export function isLinkPayload(payload: CitationPayload): boolean {
    return (
        payload.citationType !== "tool" &&
        payload.citationType !== "history" &&
        (Boolean(payload.isLink) || !payload.cited_text)
    );
}

export function formatHistoryTime(epochSeconds: number): string {
    return new Date(epochSeconds * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export type SerializedCitationNode = Spread<
    {
        citationData: CitationPayload;
        type: "citation";
        version: 1;
    },
    SerializedLexicalNode
>;

export class CitationNode extends DecoratorNode<React.JSX.Element> {
    __citationData: CitationPayload;

    static getType(): string {
        return "citation";
    }

    static clone(node: CitationNode): CitationNode {
        return new CitationNode(node.__citationData, node.__key);
    }

    constructor(citationData: CitationPayload, key?: NodeKey) {
        super(key);
        this.__citationData = citationData;
    }

    createDOM(): HTMLElement {
        // Links render inline (blue link); citations render as a block quote.
        return document.createElement(isLinkPayload(this.__citationData) ? "span" : "div");
    }

    updateDOM(): false {
        return false;
    }

    isInline(): boolean {
        return isLinkPayload(this.__citationData);
    }

    getCitationData(): CitationPayload {
        return this.__citationData;
    }

    setCitationData(citationData: CitationPayload): void {
        const writable = this.getWritable();
        writable.__citationData = citationData;
    }

    static importJSON(serializedNode: SerializedCitationNode): CitationNode {
        const { citationData } = serializedNode;
        const node = $createCitationNode(citationData);
        return node;
    }

    exportJSON(): SerializedCitationNode {
        return {
            citationData: this.__citationData,
            type: "citation",
            version: 1,
        };
    }

    getTextContent(): string {
        if (this.__citationData.citationType === "artifact") {
            return `[${this.__citationData.artifact_id}]`;
        }
        if (this.__citationData.citationType === "event") {
            return `[${this.__citationData.event_id}]`;
        }
        if (this.__citationData.citationType === "document") {
            return `[${this.__citationData.document_id}]`;
        }
        if (this.__citationData.citationType === "history") {
            return `[${formatHistoryTime(this.__citationData.start_time)}]`;
        }
        return `[${this.__citationData.document.id}]`;
    }

    decorate(): React.JSX.Element {
        return <CitationComponent citationData={this.__citationData} />;
    }
}

const CitationComponent = observer(function CitationComponent({ citationData: payload }: { citationData: CitationPayload }) {
    const streamingQuote = React.useContext(StreamingQuoteContext);
    const citationData = payload.cited_text === STREAMING_QUOTE ? { ...payload, cited_text: streamingQuote } : payload;

    const handleMouseEnter = () => {
        // Dispatch custom event for citation hover
        const event = new CustomEvent("citation-hover", {
            detail: {
                ...citationData,
                action: "enter"
            },
            bubbles: true,
        });
        document.dispatchEvent(event);
    };

    const handleMouseLeave = () => {
        // Dispatch custom event for citation hover end
        const event = new CustomEvent("citation-hover", {
            detail: {
                ...citationData,
                action: "leave"
            },
            bubbles: true,
        });
        document.dispatchEvent(event);
    };

    const handleClick = () => {
        // Dispatch custom event for citation click
        const event = new CustomEvent("citation-click", {
            detail: {
                ...citationData,
            },
            bubbles: true,
        });
        document.dispatchEvent(event);
    };

    let tooltip = "";
    let displayText = "";
    // Resolved object name (undefined until the object is loaded into chatState).
    let objectName: string | undefined;
    let objectId: number | undefined;
    let objectKey: { type: "artifact" | "event" | "document"; id: number } | undefined;

    if (citationData.citationType === "artifact") {
        const artifactObj = chatState.chatObjects.get({ type: "artifact", id: citationData.artifact_id });
        objectName = artifactObj?.label ?? (artifactObj as any)?.artifact?.variable_name;
        objectId = citationData.artifact_id;
        objectKey = { type: "artifact", id: citationData.artifact_id };
        tooltip = objectName ?? `Artifact ${citationData.artifact_id}`;
        displayText = citationData.cited_text || tooltip;
    } else if (citationData.citationType === "event") {
        const eventObj = chatState.chatObjects.get({ type: "event", id: citationData.event_id });
        objectName = eventObj?.label;
        objectId = citationData.event_id;
        objectKey = { type: "event", id: citationData.event_id };
        tooltip = objectName ?? `Event ${citationData.event_id}`;
        displayText = citationData.cited_text || tooltip;
    } else if (citationData.citationType === "document") {
        const documentObj = chatState.chatObjects.get({ type: "document", id: citationData.document_id });
        objectName = documentObj?.label ?? (documentObj as any)?.document?.variable_name;
        objectId = citationData.document_id;
        objectKey = { type: "document", id: citationData.document_id };
        tooltip = objectName ?? `Document ${citationData.document_id}`;
        displayText = citationData.cited_text || tooltip;
    } else if (citationData.citationType === "history") {
        tooltip = formatHistoryTime(citationData.start_time);
        displayText = citationData.cited_text || tooltip;
    } else {
        tooltip = `${citationData.document.type}: ${citationData.document.name}`;
        displayText = citationData.cited_text || tooltip;
    }
    if (payload.cited_text === STREAMING_QUOTE) displayText = streamingQuote;

    // Link: an object reference with no quoted text. Render as an inline
    // underlined blue link showing the object's name (falling back to its id
    // until the name loads). Load the object into chatState if it isn't there.
    const link = isLinkPayload(payload);

    React.useEffect(() => {
        // If the referenced object isn't loaded yet, fetch it so its name can render.
        if (link && objectKey && chatState.chatObjects.get(objectKey) === undefined) {
            chatState.getObjectByKey(objectKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link, objectName, objectId]);

    if (link) {
        return (
            <span
                className="citation-link"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                title={tooltip}
            >
                {objectName ?? objectId}
            </span>
        );
    }

    return (
        <div className="flex flex-col items-start">
            <QuoteIcon size={32} rotation={180} />
            <div
                className="citation-toggle"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                title={tooltip}
            >
                <p className="citation-text">{stripInlineMarkdown(displayText)}</p>
                {citationData.citationType === "history" && <p className="text-xs opacity-60">{tooltip}</p>}
            </div>

        </div>
    );
});

export function $createCitationNode(citationData: CitationPayload): CitationNode {
    return new CitationNode(citationData);
}

export function $isCitationNode(node: LexicalNode | null | undefined): node is CitationNode {
    return node instanceof CitationNode;
}
